import React from 'react'
import { Avatar, Flex, Label, Link, Text } from '@workwhile/ui'
import { format, formatDistanceToNowStrict } from 'date-fns'
import type { UserProfile } from 'api/user/profile'
import { CopyableCode } from 'components/Shared/CopyableCode'
import { CopyableLink } from 'components/Shared/CopyableLink'
import { TimeConversionPopover } from 'components/Shared/TimeConversion'
import { defs } from 'components/Shared/defs'
import { getLocalTimezone } from 'lib/time'

type UserDetailHeaderProps = {
  user?: UserProfile
  userId: number
  isLoading: boolean
}

type TagConfig = {
  label: string
  variant: 'error' | 'information' | 'success' | 'warning'
}

const USER_TAG_CONFIG = {
  admin: { label: 'WorkWhile Admin', variant: 'warning' },
  system: { label: 'System', variant: 'warning' },
  demo: { label: 'Demo', variant: 'warning' },
} satisfies Record<string, TagConfig>

const STATUS_TAG_CONFIG: Record<UserProfile['status'], TagConfig> = {
  active: { label: 'Active', variant: 'success' },
  account_created: { label: 'Account Created', variant: 'information' },
  blocked: { label: 'Blocked', variant: 'error' },
  deleted: { label: 'Deleted', variant: 'error' },
  disabled: { label: 'Disabled', variant: 'error' },
  not_in_market: { label: 'Not In Market', variant: 'information' },
  onboarding: { label: 'Onboarding', variant: 'information' },
  suspended: { label: 'Suspended', variant: 'error' },
}

const UserTag = ({ label, variant }: TagConfig) => (
  <Label size="small" variant={variant}>
    {label}
  </Label>
)

const UserActivity = ({ user }: { user: UserProfile }) => (
  <Flex alignItems="center" gap={2} flexWrap="wrap">
    {user.createdAt ? (
      <Text
        fontSize={1}
        color="lightText"
        title={format(new Date(user.createdAt), 'MMM d, yyyy, h:mm a')}
      >
        Created{' '}
        {formatDistanceToNowStrict(new Date(user.createdAt), {
          addSuffix: true,
        })}
      </Text>
    ) : null}
    {user.createdAt && user.lastSeen ? (
      <Text fontSize={1} color="lightText">
        ·
      </Text>
    ) : null}
    {user.lastSeen ? (
      <Text fontSize={1} color="lightText">
        Last seen{' '}
        <TimeConversionPopover
          value={user.lastSeen}
          primaryTimeZone={user.timezone}
          zones={[
            { timeZone: user.timezone, context: 'User' },
            { timeZone: getLocalTimezone(), context: 'Your computer' },
            { timeZone: 'UTC' },
          ]}
        >
          <Text as="span" fontSize={1} color="lightText">
            {formatDistanceToNowStrict(new Date(user.lastSeen), {
              addSuffix: true,
            })}
          </Text>
        </TimeConversionPopover>
      </Text>
    ) : null}
  </Flex>
)

const UserRoleDetails = ({ user }: { user: UserProfile }) => {
  // Employer profile takes precendence over user work profile. Most employers have both profiles, but worker profile is not relevant.
  if (user.hasCompanyRole) {
    return (
      <Flex alignItems="center" gap={2} flexWrap="wrap">
        <Text fontWeight="bold">Employer Profile:</Text>
        {user.employerRole ? (
          <>
            <CopyableCode value={user.employerRole} />
            <Text>·</Text>
          </>
        ) : null}
        <Text>Companies:</Text>
        {user.whitelistedCompanyIds.length > 0 ? (
          user.whitelistedCompanyIds.map((companyId) => (
            <CopyableLink
              key={companyId}
              label={companyId}
              link={`/companies/${companyId}`}
            />
          ))
        ) : (
          <Text>None</Text>
        )}
      </Flex>
    )
  }

  if (user.hasWorkerProfile) {
    return (
      <Flex alignItems="center" gap={2} flexWrap="wrap">
        <Text fontWeight="bold">Worker Profile:</Text>
        <Text>
          {user.shiftCount} shift{user.shiftCount === 1 ? '' : 's'}
        </Text>
      </Flex>
    )
  }

  return null
}

export const UserDetailHeader = ({
  user,
  userId,
  isLoading,
}: UserDetailHeaderProps) => (
  <Flex
    alignItems="center"
    justifyContent="space-between"
    mb={defs.marginM}
    flexWrap="wrap"
    gap={2}
  >
    <Flex alignItems="center" gap={3}>
      <Avatar
        size="xlarge"
        showLoader={isLoading}
        user={
          user
            ? {
                userId: String(userId),
                name: user.name,
                photoUrl: user.profilePicUrl,
              }
            : undefined
        }
      />
      <Flex flexDirection="column" alignItems="flex-start" gap={1}>
        <Flex alignItems="center" gap={2} flexWrap="wrap">
          <Text as="h1" fontSize={4} fontWeight="bold" my={0}>
            {user?.name ?? `User ${userId}`}
          </Text>
          <CopyableCode value={String(userId)} />
          {user?.firebaseId ? <CopyableCode value={user.firebaseId} /> : null}
          {user?.hasWorkwhileAdmin ? (
            <UserTag {...USER_TAG_CONFIG.admin} />
          ) : null}
          {user ? <UserTag {...STATUS_TAG_CONFIG[user.status]} /> : null}
          {user?.shadowBlocked ? (
            <UserTag label="Shadow Blocked" variant="error" />
          ) : null}
          {user && user.userType !== 'standard' ? (
            <UserTag {...USER_TAG_CONFIG[user.userType]} />
          ) : null}
        </Flex>
        {user && !user.hasWorkwhileAdmin ? (
          <UserRoleDetails user={user} />
        ) : null}
        {user ? <UserActivity user={user} /> : null}
      </Flex>
    </Flex>
    <Link to={`/users/${userId}`}>← Back to old user view</Link>
  </Flex>
)
